<script setup lang="ts">
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { requirements } from '@/utils/formRequirements';
import z from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import type { NonNullableFields } from '@/utils';
import { VForm, VTextarea, VBtn } from 'vuetify/components';

export interface ProjectHoldFormValues {
    reason: string;
}

const props = defineProps<{
    initialValues?: ProjectHoldFormValues;
}>();

const { t } = useI18n({
    messages: {
        nl: {
            reason: "Wat is de reden van het on-hold zetten van dit @.lower:{'project'}?",
            confirm: 'Zet on hold',
        },
        en: {
            reason: 'What is the reason to put this @.lower:project on hold?',
            confirm: 'Put on hold',
        },
    },
});

const emit = defineEmits<{
    submit: [values: NonNullableFields<ProjectHoldFormValues>];
    cancel: [];
}>();

const validationSchema = z.object({
    reason: z
        .string()
        .min(requirements.project.hold_reason.min, t('error.required_field'))
        .max(
            requirements.project.hold_reason.max,
            t('error.maximum_characters', requirements.project.hold_reason.max)
        ),
});

const { handleSubmit } = useForm({
    validationSchema: toTypedSchema(validationSchema),
    initialValues: props.initialValues ? props.initialValues : { reason: '' },
});

const reason = useField<string>('reason');

const submit = handleSubmit((values) => {
    emit('submit', values as NonNullableFields<ProjectHoldFormValues>);
});
</script>

<template>
    <VForm class="d-flex flex-column ga-3" @submit.prevent="submit">
        <p>{{ t('reason') }}</p>
        <VTextarea
            v-model="reason.value.value"
            :error-messages="reason.errorMessage.value"
            :counter="requirements.project.hold_reason.max"
        />

        <div class="d-flex justify-end ga-3 mt-5">
            <VBtn variant="text" @click="emit('cancel')">{{
                t('cancel')
            }}</VBtn>
            <VBtn type="submit" variant="elevated">{{ t('confirm') }}</VBtn>
        </div>
    </VForm>
</template>
